import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, ArrowRight, Store as StoreIcon, Truck, BadgeCheck } from 'lucide-react';
import { CATEGORIES, DISTRICTS, IMAGES, PLAN_RANK, DEFAULT_HOME_LAYOUT } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/commerce/ProductCard';
import { StoreCard } from '../components/commerce/StoreCard';
import { fileUrl } from '../lib/upload';

function Section({ title, sub, actionTo, actionLabel, children, testId }) {
  return (
    <section data-testid={testId} className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-kelate-ink">{title}</h2>
          {sub && <p className="text-sm text-kelate-soft">{sub}</p>}
        </div>
        {actionTo && (
          <Link to={actionTo} data-testid={`${testId}-see-all`} className="flex items-center gap-1 text-sm font-medium text-kelate-wood hover:text-kelate-darkwood">
            {actionLabel || 'See all'} <ArrowRight size={14} />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { partners, campaign, customMerchants, customProducts, homeLayout, homeHero } = useApp();
  const hero = {
    kicker: homeHero?.kicker || "Kelantan's own marketplace",
    title: homeHero?.title || 'Discover Kelantan, all in one place.',
    subtitle: homeHero?.subtitle || 'Shop local products, discover trusted businesses and support Kelantan brands.',
    image: homeHero?.image ? fileUrl(homeHero.image) : IMAGES.batikCraft,
    cardImage: homeHero?.cardImage ? fileUrl(homeHero.cardImage) : IMAGES.nasiKerabu,
    cardText: homeHero?.cardText || 'Local favourites from Kelantan',
  };
  const allMerchants = customMerchants;
  const [q, setQ] = useState('');
  const location = localStorage.getItem('kelate_location') || 'Kota Bharu';
  const deals = customProducts.filter((p) => (p.badges || []).includes('SALE'));
  const local = customProducts.filter((p) => p.madeInKelantan);
  const nearYou = customProducts.filter((p) => p.district === location);
  const bestSelling = [...customProducts].sort((a, b) => b.sold - a.sold).slice(0, 4);
  const newArrivals = customProducts.filter((p) => (p.badges || []).includes('NEW'));

  const layout = (homeLayout && homeLayout.length ? homeLayout : DEFAULT_HOME_LAYOUT);
  const visible = {
    deals: deals.length > 0,
    campaign: campaign.active,
    madeInKelantan: local.length > 0,
    nearYou: customProducts.length > 0,
    featuredStores: allMerchants.length > 0,
    bestSelling: bestSelling.length > 0,
    newArrivals: newArrivals.length > 0,
    partners: partners.length > 0,
  };

  const grid4 = (items) => (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
      {items.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );

  const renderSection = (id) => {
    switch (id) {
      case 'hero':
        return (
          <div className="border-b border-kelate-line bg-kelate-surface">
            <div className="mx-auto grid max-w-[1280px] items-center gap-8 px-4 py-14 lg:grid-cols-2 lg:py-20">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-kelate-wood" data-testid="hero-kicker">{hero.kicker}</p>
                <h1 className="mt-3 font-display text-4xl font-semibold leading-tight text-kelate-darkwood sm:text-5xl lg:text-6xl" data-testid="hero-title">
                  {hero.title}
                </h1>
                <p className="mt-4 max-w-md text-base text-kelate-soft md:text-lg" data-testid="hero-subtitle">
                  {hero.subtitle}
                </p>
                <form
                  onSubmit={(e) => { e.preventDefault(); navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search'); }}
                  className="mt-6 flex max-w-lg items-center gap-2"
                >
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kelate-soft" />
                    <input
                      data-testid="hero-search-input"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search products, stores or services..."
                      className="h-11 w-full rounded-sm border border-kelate-line bg-kelate-bg pl-9 pr-3 text-sm outline-none focus:border-kelate-wood"
                    />
                  </div>
                  <button data-testid="hero-search-btn" className="h-11 rounded-sm bg-kelate-darkwood px-5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
                    Search
                  </button>
                </form>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-kelate-soft">
                  <MapPin size={12} className="text-kelate-wood" /> Delivering in <span className="font-medium text-kelate-ink">{location}</span> and across Kelantan
                </div>
                <div className="mt-6 flex flex-wrap gap-5 text-xs text-kelate-soft">
                  <span className="flex items-center gap-1.5"><Truck size={13} className="text-kelate-sage" /> Pay on delivery</span>
                  <span className="flex items-center gap-1.5"><BadgeCheck size={13} className="text-kelate-sage" /> Verified local merchants</span>
                  <span className="flex items-center gap-1.5"><StoreIcon size={13} className="text-kelate-sage" /> KB delivery from RM3</span>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <img src={hero.image} alt={hero.title} data-testid="hero-image" className="h-80 w-full rounded-3xl object-cover shadow-[0_12px_32px_rgba(81,68,55,0.15)]" />
                <div className="absolute -bottom-5 -left-5 w-44 overflow-hidden rounded-2xl bg-kelate-surface shadow-[0_8px_24px_rgba(81,68,55,0.18)]">
                  <img src={hero.cardImage} alt="" className="h-24 w-full object-cover" />
                  <div className="p-2 text-xs font-medium text-kelate-ink" data-testid="hero-card-text">{hero.cardText}</div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'categories':
        return (
          <Section title="Categories" testId="section-categories" actionTo="/marketplace">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 lg:grid-cols-10">
              {CATEGORIES.slice(0, 10).map((c) => (
                <Link
                  key={c}
                  to={`/search?category=${encodeURIComponent(c)}`}
                  data-testid={`category-${c.toLowerCase().replace(/[\s&]+/g, '-')}`}
                  className="flex items-center justify-center rounded-2xl border border-kelate-line/70 bg-kelate-surface px-2 py-3 text-center text-xs text-kelate-ink shadow-[0_1px_3px_rgba(81,68,55,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(81,68,55,0.12)]"
                >
                  {c}
                </Link>
              ))}
            </div>
          </Section>
        );
      case 'deals':
        return (
          <Section title="Today's Deals" sub="Discounted picks from local merchants" testId="section-deals" actionTo="/promotions">
            {grid4(deals)}
          </Section>
        );
      case 'campaign':
        return (
          <section data-testid="section-food-week" className="mx-auto max-w-[1280px] px-4 py-4">
            <div className="flex flex-col items-start justify-between gap-4 rounded-3xl bg-kelate-darkwood p-6 shadow-[0_10px_28px_rgba(81,68,55,0.2)] sm:flex-row sm:items-center">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-kelate-wood">Campaign</div>
                <h3 className="mt-1 font-display text-2xl font-semibold text-kelate-surface">{campaign.title}</h3>
                <p className="mt-1 text-sm text-[#CBBFA9]">{campaign.headline} {campaign.description}</p>
              </div>
              <Link to="/campaign/food-week" data-testid="food-week-cta" className="rounded-xl bg-kelate-surface px-5 py-2.5 text-sm font-medium text-kelate-darkwood hover:bg-kelate-bg">
                {campaign.cta}
              </Link>
            </div>
          </section>
        );
      case 'madeInKelantan':
        return (
          <Section title="Made in Kelantan" sub="Proudly crafted by Kelantan brands" testId="section-made-in-kelantan" actionTo="/made-in-kelantan">
            {grid4(local.slice(0, 4))}
          </Section>
        );
      case 'nearYou':
        return (
          <Section title={`Popular Near You`} sub={`Around ${location}`} testId="section-near-you" actionTo="/explore">
            {grid4(nearYou.length ? nearYou : customProducts.slice(0, 4))}
          </Section>
        );
      case 'featuredStores':
        return (
          <Section title="Featured Stores" testId="section-featured-stores" actionTo="/marketplace">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...allMerchants].sort((a, b) => (PLAN_RANK[a.plan] ?? 9) - (PLAN_RANK[b.plan] ?? 9)).slice(0, 4).map((m) => <StoreCard key={m.id} merchant={m} />)}
            </div>
          </Section>
        );
      case 'bestSelling':
        return (
          <Section title="Best Selling" testId="section-best-selling" actionTo="/search?sort=popular">
            {grid4(bestSelling)}
          </Section>
        );
      case 'newArrivals':
        return (
          <Section title="New Arrivals" testId="section-new-arrivals" actionTo="/search?sort=latest">
            {grid4(newArrivals)}
          </Section>
        );
      case 'explore':
        return (
          <Section title="Explore Kelantan" sub="Browse by district" testId="section-explore" actionTo="/explore">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {DISTRICTS.map((d) => (
                <Link
                  key={d}
                  to={`/explore/${encodeURIComponent(d.toLowerCase().replace(/\s+/g, '-'))}`}
                  data-testid={`district-${d.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-2xl border border-kelate-line/70 bg-kelate-surface px-3 py-3.5 text-center text-sm text-kelate-ink shadow-[0_1px_3px_rgba(81,68,55,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(81,68,55,0.12)]"
                >
                  {d}
                </Link>
              ))}
            </div>
          </Section>
        );
      case 'partners':
        return (
          <Section title="Our Collaboration Partners" sub="Growing Kelantan together" testId="section-partners" actionTo="/partners">
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
              {partners.map((p) => (
                <Link
                  key={p.id}
                  to={`/partners/${p.id}`}
                  data-testid={`home-partner-${p.id}`}
                  className="flex flex-col items-center gap-2 rounded-2xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(81,68,55,0.14)]"
                >
                  {p.profilePhoto || p.logo ? (
                    <img src={fileUrl(p.profilePhoto || p.logo)} alt={p.name} className="h-12 w-12 rounded-xl object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-kelate-darkwood font-display text-lg font-semibold text-kelate-surface">{p.name[0]}</span>
                  )}
                  <span className="text-center text-[11px] font-medium leading-tight text-kelate-ink">{p.name}</span>
                </Link>
              ))}
            </div>
          </Section>
        );
      case 'merchantCta':
        return (
          <section data-testid="section-merchant-cta" className="mx-auto max-w-[1280px] px-4 py-8">
            <div className="rounded-3xl bg-kelate-surface p-8 text-center shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
              <h3 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">Grow your business with KELATE</h3>
              <p className="mx-auto mt-2 max-w-lg text-sm text-kelate-soft">
                List your business from RM10/month. Sell on the marketplace, let KELATE handle delivery and payment collection.
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-6 text-xs text-kelate-soft">
                <span><b className="text-kelate-ink">LIST</b> RM10</span>
                <span><b className="text-kelate-ink">STORE</b> RM50</span>
                <span><b className="text-kelate-ink">BOOST</b> RM100</span>
                <span><b className="text-kelate-ink">BRAND</b> RM200</span>
              </div>
              <Link to="/merchant/register" data-testid="become-merchant-cta" className="mt-6 inline-block rounded-sm bg-kelate-darkwood px-6 py-3 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
                Become a Merchant
              </Link>
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="home-page">
      {layout.filter((s) => s.enabled && visible[s.id] !== false).map((s) => (
        <React.Fragment key={s.id}>{renderSection(s.id)}</React.Fragment>
      ))}
    </div>
  );
}
